/**
 * @documental
 * @export TooltipStyle
 * @import
 * import { TooltipStyle } from "capitalrx-components-2.0/lib/general";
 */

import { FC, useMemo, PropsWithChildren } from "react";
import { TooltipStyleContext, TooltipStyleContextValue } from "./TooltipStyleContext";

/**
 * The `TooltipStyle` component properties.
 */
export interface TooltipStyleProps {
  /**
   * The appearance of the child tooltips. Defaults to `"dark"` if not specified.
   */
  readonly appearance?: "light" | "dark";

  /**
   * The alignment of child tooltips relative to the element. If `"top"`, `"bottom"`, `"left"` or `"right"` are
   * chosen, then the tooltip will attach itself to the specified edge of the target element. If `"cursor"` is
   * specified then the tooltip will track the mouse cursor position when hovering over the target element.
   * Defaults to `"cursor"` if not specified.
   */
  readonly alignment?: "top" | "bottom" | "left" | "right" | "cursor";
}

/**
 * Component which applies a specified Tooltip appearance and alignment to all child tooltips. Can be overriden
 * by individual `Tooltip` instances, or by other descendent `TooltipStyle` components.
 */
export const TooltipStyle: FC<PropsWithChildren<TooltipStyleProps>> = (props) => {
  const { children, appearance = "dark", alignment = "cursor" } = props;

  const contextValue = useMemo(
    (): TooltipStyleContextValue => ({
      appearance,
      alignment,
    }),
    [appearance, alignment],
  );

  return <TooltipStyleContext.Provider value={contextValue}>{children}</TooltipStyleContext.Provider>;
};
